import { parseSearchParam } from "@/src/common/utils"
import { useAnalytics } from "@/src/sb/components/Analytics"
import { SUBSCRIPTIONS_PAGE_SUBSCRIBERS_DARK_THEME } from "@/src/sb/constants"
import { pages } from "@/src/sb/constants/copy"
import { userHasActiveSubscription } from "@/src/sb/core/utils/helpers"
import useRequestDemo from "@/src/sb/hooks/useRequestDemo"
import { useUser } from "@/src/sb/hooks/useUser"
import classNames from "classnames"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"

import Button from "../Button"

type PaywallProps = {
  requireSubscription?: boolean
  extraEventProps?: Record<string, any>
}

const Paywall = ({ requireSubscription, extraEventProps }: PaywallProps) => {
  const { userType, data: userData } = useUser()

  const { asPath: articleURL } = useRouter()
  const analytics = useAnalytics()
  const requestDemo = useRequestDemo()

  const queriesToForward = parseSearchParam({
    next: articleURL
  })

  const noActiveSubscription = !userHasActiveSubscription({ userType })
  const userIsAuthenticated = Boolean(userData)
  const showSubscribeCopy = requireSubscription && noActiveSubscription

  return (
    <div className="font-jakarta dark:bg-stears_black dark:text-stears_white -mt-3">
      <section className="px-6 pt-12 pb-6 space-y-8">
        <header>
          <h5 className="mb-2 font-medium text-lg md:text-2xl text-gray-800 dark:text-stears_white">
            Africa’s most comprehensive financial database
          </h5>
          <p className="font-light text-gray-600 md:text-lg dark:text-gray-400">
            {showSubscribeCopy
              ? "Data on African companies, Transactions, countries and industries"
              : "Request Demo or sign in to finish reading."}
          </p>
        </header>

        <div className="text-center md:text-left">
          <div className="inline-flex flex-col md:flex-row md:items-center gap-3">
            {showSubscribeCopy || !userIsAuthenticated ? (
              <Button
                type="cta"
                roundness="edge"
                noWrap
                stretch
                href={`${requestDemo.url}?${queriesToForward.toString()}`}
                onClick={() =>
                  requestDemo.trackClickCorporateQuote({ context: "paywall" })
                }
              >
                <span className="text-lg">Request a Trial</span>
              </Button>
            ) : null}

            {!userIsAuthenticated ? (
              <Button
                type="primary"
                roundness="edge"
                stretch
                href={`${pages.signin}?${queriesToForward.toString()}`}
                onClick={() => {
                  analytics?.clickSignin({
                    ...extraEventProps,
                    context: "paywall",
                    type: "button"
                  })
                }}
              >
                <span className="text-lg">Sign in</span>
              </Button>
            ) : null}
          </div>
        </div>

        <section>
          <h5 className="mb-6 font-medium text-lg md:text-xl">
            Trusted by global funds & corporates
          </h5>

          <section
            className={classNames(
              "flex flex-wrap gap-4 items-center place-content-center w-full justify-start"
            )}
          >
            {SUBSCRIPTIONS_PAGE_SUBSCRIBERS_DARK_THEME.slice(0, 7).map(
              (imageName) => {
                const image =
                  require(`@/public/assets/img/brands/${imageName}`).default

                return (
                  <div
                    className={classNames("inline-flex justify-center")}
                    key={imageName}
                  >
                    <Image
                      src={image}
                      width={(image?.width ?? 0) / 2}
                      height={(image?.height ?? 0) / 2}
                      alt="subscriber"
                      className="grayscale"
                      loading={"lazy"}
                    />
                  </div>
                )
              }
            )}
          </section>
        </section>

        <footer className="text-xs md:text-base space-y-2">
          {!userIsAuthenticated ? (
            requireSubscription ? null : (
              <p className={"text-gray-600"}>
                Want to learn more about our Corporate Licence?{" "}
                <Link
                  href={`${requestDemo.url}?${queriesToForward.toString()}`}
                  className="stears-link"
                  onClick={() => {
                    requestDemo.trackClickCorporateQuote({ context: "paywall" })
                  }}
                >
                  Request Demo
                </Link>
              </p>
            )
          ) : null}
        </footer>
      </section>
    </div>
  )
}

export default Paywall
